import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import { ApiError } from "../lib/api";
import { qk, useEntries, useEntry } from "../lib/queries";
import { isInFlight, type EntryFilters } from "../lib/types";
import { useUploads } from "../lib/uploadQueue";
import { CaptureButton } from "../components/CaptureButton";
import { DevelopingCard } from "../components/DevelopingCard";
import { EmptyState } from "../components/EmptyState";
import { EntryCard } from "../components/EntryCard";
import { ErrorState } from "../components/ErrorState";
import { EntryCardSkeleton, LoadingAnnouncement } from "../components/Skeleton";

type Scope = "all" | "favourites" | "unfiled";

/**
 * The feed: everything you've found, newest first, with anything currently in
 * the developer tray pinned to the top.
 */
export function Scrapbook() {
  const [params, setParams] = useSearchParams();
  const { items, trackedIds } = useUploads();

  const scope: Scope = params.get("unfiled") === "true"
    ? "unfiled"
    : params.get("favourite") === "true"
      ? "favourites"
      : "all";
  const query = params.get("q") ?? "";

  const filters = useMemo<EntryFilters>(() => {
    const next: EntryFilters = {};
    if (scope === "unfiled") next.unfiled = true;
    if (scope === "favourites") next.favourite = true;
    if (query.trim().length > 0) next.q = query.trim();
    return next;
  }, [scope, query]);

  const feed = useEntries(filters);

  const setScope = (nextScope: Scope) => {
    const next = new URLSearchParams(params);
    next.delete("unfiled");
    next.delete("favourite");
    if (nextScope === "unfiled") next.set("unfiled", "true");
    if (nextScope === "favourites") next.set("favourite", "true");
    setParams(next, { replace: true });
  };

  const setQuery = (value: string) => {
    const next = new URLSearchParams(params);
    if (value.trim().length > 0) next.set("q", value.trim());
    else next.delete("q");
    setParams(next, { replace: true });
  };

  // Uploads still on their way up, plus entries the server is still working on.
  const uploading = items.filter((item) => item.status !== "error");
  const tracked = new Set(trackedIds);

  const entries = (feed.data?.pages ?? [])
    .flatMap((page) => page.items)
    .filter((entry) => !tracked.has(entry.id));

  const nothingAtAll =
    feed.isSuccess && entries.length === 0 && tracked.size === 0 && uploading.length === 0;
  const filtered = scope !== "all" || query.trim().length > 0;

  return (
    <div>
      <header className="mb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="display-2 text-[2.25rem] leading-none text-ink sm:text-5xl">
              The scrapbook
            </h1>
            <p className="mt-2 font-prose text-ink-soft">
              Every page you've mounted, most recent first.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-3 border-b-2 border-rule pb-3">
          <div className="flex items-center">
            <ScopeTab current={scope} value="all" onSelect={setScope}>
              All finds
            </ScopeTab>
            <ScopeTab current={scope} value="favourites" onSelect={setScope}>
              Kept
            </ScopeTab>
            <ScopeTab current={scope} value="unfiled" onSelect={setScope}>
              Unfiled
            </ScopeTab>
          </div>

          <SearchField initial={query} onSubmit={setQuery} className="ms-auto" />
        </div>

        {scope === "unfiled" ? (
          <p className="mt-3 max-w-prose font-prose leading-relaxed text-ink-soft">
            These finds have no place attached, so they haven't joined a collection. Open one and add
            a place — Foundling files it from there.
          </p>
        ) : null}
      </header>

      {feed.isError && entries.length === 0 ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Still going up. */}
        {uploading.map((item) => (
          <DevelopingCard
            key={item.localId}
            seedId={item.entryId ?? item.localId}
            previewUrl={item.previewUrl}
            phase={item.status}
            progress={item.status === "uploading" ? item.progress : null}
            filename={item.name}
          />
        ))}

        {/* In the darkroom. */}
        {trackedIds.map((id) => (
          <InFlightEntry key={id} id={id} />
        ))}

        {feed.isPending
          ? [0, 1, 2, 3, 4, 5].map((index) => <EntryCardSkeleton key={index} />)
          : entries.map((entry, index) => (
              <EntryCard key={entry.id} entry={entry} eager={index < 3} />
            ))}
      </div>

      {feed.isPending ? <LoadingAnnouncement>Opening your scrapbook…</LoadingAnnouncement> : null}

      {nothingAtAll ? (
        filtered ? (
          <FilteredEmpty scope={scope} query={query} onClear={() => setParams({}, { replace: true })} />
        ) : (
          <FirstRun />
        )
      ) : null}

      {feed.hasNextPage ? (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => void feed.fetchNextPage()}
            disabled={feed.isFetchingNextPage}
            className="touch border border-ink bg-card px-5 py-2.5 font-label text-sm tracking-wide text-ink transition-colors hover:bg-paper-deep disabled:opacity-60"
          >
            {feed.isFetchingNextPage ? "Turning the page…" : "Earlier finds"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * One entry that hasn't finished developing.
 *
 * Polls itself every couple of seconds and stops the moment it reaches a
 * terminal status, plays the reveal, then hands the entry back to the feed.
 */
function InFlightEntry({ id }: { id: string }) {
  const client = useQueryClient();
  const { previews, untrack } = useUploads();
  const { data: entry, error } = useEntry(id, { poll: true });
  const [landed, setLanded] = useState(false);

  const preview = previews[id] ?? null;
  const status = entry?.status;

  // Terminal: refresh everything the new find touches, play the reveal, then
  // let the ordinary feed take over.
  useEffect(() => {
    if (!status || isInFlight(status)) return;
    setLanded(true);
    void client.invalidateQueries({ queryKey: ["entries"] });
    void client.invalidateQueries({ queryKey: qk.collections });
    void client.invalidateQueries({ queryKey: ["collection"] });
    void client.invalidateQueries({ queryKey: qk.me });
    const timer = window.setTimeout(() => untrack(id), 2400);
    return () => window.clearTimeout(timer);
  }, [status, client, id, untrack]);

  // Deleted from under us, or never existed.
  useEffect(() => {
    if (error instanceof ApiError && error.isNotFound) untrack(id);
  }, [error, id, untrack]);

  if (!entry) {
    return <DevelopingCard seedId={id} previewUrl={preview} phase="queued" />;
  }

  if (isInFlight(entry.status)) {
    return <DevelopingCard seedId={id} previewUrl={preview} phase={entry.status} />;
  }

  return <EntryCard entry={entry} revealing={landed} eager />;
}

/* -------------------------------------------------------------------------- */

function ScopeTab({
  current,
  value,
  onSelect,
  children,
}: {
  current: Scope;
  value: Scope;
  onSelect: (scope: Scope) => void;
  children: string;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(value)}
      className={clsx(
        "touch -ml-px border px-3 py-1.5 font-label text-[0.6875rem] tracking-widest uppercase transition-colors first:ml-0",
        active
          ? "border-ink bg-ink text-paper"
          : "border-rule text-ink-soft hover:bg-paper-deep hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function SearchField({
  initial,
  onSubmit,
  className,
}: {
  initial: string;
  onSubmit: (value: string) => void;
  className?: string;
}) {
  const [value, setValue] = useState(initial);

  useEffect(() => setValue(initial), [initial]);

  return (
    <form
      role="search"
      className={clsx("flex items-stretch", className)}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value);
      }}
    >
      <label htmlFor="scrapbook-search" className="sr-only">
        Search your finds
      </label>
      <input
        id="scrapbook-search"
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Search names, notes, tags"
        className="w-44 border border-rule bg-card px-2.5 py-1.5 font-prose text-sm text-ink placeholder:text-ink-soft/60 focus:border-ochre focus:outline-none sm:w-56"
      />
      <button
        type="submit"
        className="touch -ml-px border border-rule px-3 font-label text-[0.6875rem] tracking-widest text-ink-soft uppercase transition-colors hover:bg-paper-deep hover:text-ink"
      >
        Look
      </button>
    </form>
  );
}

/* -------------------------------------------------------------------------- */

/** The first page of an empty book — an invitation, not a shrug. */
function FirstRun() {
  return (
    <EmptyState
      title="An empty book, waiting"
      motif="sprig"
      action={<CaptureButton variant="inline" label="Photograph your first find" />}
    >
      <p>
        Foundling starts the moment you photograph something. A tree on your street, a bird on the
        wire, a door with an odd knocker — anything you'd stop and look at twice.
      </p>
      <p className="mt-3">
        Whatever it is, you'll get a page: a name, a field note, and the details a guide would print.
        Then it files itself into a collection, and tells you what else is nearby worth finding.
      </p>
    </EmptyState>
  );
}

function FilteredEmpty({
  scope,
  query,
  onClear,
}: {
  scope: Scope;
  query: string;
  onClear: () => void;
}) {
  const clear = (
    <button type="button" onClick={onClear} className="link touch font-label text-sm">
      Show every find
    </button>
  );

  if (query.trim().length > 0) {
    return (
      <EmptyState title="Nothing under that name" motif="search" action={clear}>
        <p>
          No find matches “{query.trim()}”. Foundling searches names, field notes, your own notes and
          tags — so a broader word usually turns something up.
        </p>
      </EmptyState>
    );
  }

  if (scope === "unfiled") {
    return (
      <EmptyState title="Everything has a place" motif="list" action={clear}>
        <p>
          Every find in your book has a place attached, which means every one of them has been filed
          into a collection. Tidy.
        </p>
      </EmptyState>
    );
  }

  return (
    <EmptyState title="Nothing kept yet" motif="sheet" action={clear}>
      <p>
        Press the flower on any page to keep it. Kept finds gather here — the ones you'd show
        somebody.
      </p>
    </EmptyState>
  );
}
